import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/apiClient";

const BASE = "/api/admin/reports/settings/layout";

function unwrap<T>(payload: unknown): T {
  const body = payload as { data?: T } | T;
  if (body && typeof body === "object" && "data" in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export interface WidgetDescriptor {
  key: string;
  label: string;
  visible: boolean;
}

export interface WidgetDef {
  key: string;
  label: string;
}

export interface UseWidgetLayoutResult {
  /** Kullanıcı tercihine göre sıralanmış, görünür widget anahtarları. */
  visibleWidgets: string[];
  descriptors: WidgetDescriptor[];
  isSaving: boolean;
  isCustomized: boolean;
  toggleWidget: (key: string) => void;
  moveWidget: (key: string, direction: "up" | "down") => void;
  reset: () => void;
}

/**
 * Gösterge/blok düzeni tercihi — gizleme ve sıralama (R11).
 *
 * Raporlar ana sayfası kategori bloklarını sabit sırayla basıyordu; kullanıcı
 * kendisini ilgilendirmeyen kategoriyi gizleyemiyor, sık kullandığını yukarı
 * alamıyordu. `dashboardKey` hangi ekranın düzeni olduğunu ayırır.
 *
 * Panel blokları koda gömülüydü; kullanıcı kendisi için anlamsız bir bloğu
 * gizleyemiyor, sık kullandığını yukarı alamıyordu. Tercih sunucuda kullanıcı +
 * panel bazında saklanır; yüklenene kadar panel kendi varsayılan sırasıyla çalışır.
 */
export function useWidgetLayout(
  dashboardKey: string,
  widgets: WidgetDef[],
): UseWidgetLayoutResult {
  const [widgetOrder, setWidgetOrder] = useState<string[]>([]);
  const [hiddenWidgets, setHiddenWidgets] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const defaultOrder = useMemo(() => widgets.map((w) => w.key), [widgets]);

  useEffect(() => {
    // Düzen kapalıysa (anahtar yok) sunucuya hiç gitme.
    if (!dashboardKey) return;
    let cancelled = false;
    apiClient
      .get(`${BASE}/${dashboardKey}`)
      .then(({ data }) => {
        if (cancelled) return;
        const pref = unwrap<{ widgetOrder: string[]; hiddenWidgets: string[] }>(data);
        setWidgetOrder(pref?.widgetOrder ?? []);
        setHiddenWidgets(pref?.hiddenWidgets ?? []);
      })
      .catch(() => {
        // Tercih okunamazsa panel varsayılan düzeniyle çalışmaya devam etmeli.
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardKey]);

  const persist = useCallback(
    (nextOrder: string[], nextHidden: string[]) => {
      if (!dashboardKey) return;
      setIsSaving(true);
      apiClient
        .put(`${BASE}/${dashboardKey}`, { widgetOrder: nextOrder, hiddenWidgets: nextHidden })
        .catch(() => undefined)
        .finally(() => setIsSaving(false));
    },
    [dashboardKey],
  );

  // Kaydedilmiş sıra + sonradan eklenen yeni widget'lar (eski tercih yenisini yutmasın).
  const effectiveOrder = useMemo(() => {
    const known = widgetOrder.filter((key) => defaultOrder.includes(key));
    const missing = defaultOrder.filter((key) => !known.includes(key));
    return [...known, ...missing];
  }, [widgetOrder, defaultOrder]);

  const visibleWidgets = useMemo(
    () => effectiveOrder.filter((key) => !hiddenWidgets.includes(key)),
    [effectiveOrder, hiddenWidgets],
  );

  const descriptors = useMemo<WidgetDescriptor[]>(() => {
    const byKey = new Map(widgets.map((w) => [w.key, w]));
    return effectiveOrder
      .map((key) => byKey.get(key))
      .filter((w): w is WidgetDef => !!w)
      .map((w) => ({ key: w.key, label: w.label, visible: !hiddenWidgets.includes(w.key) }));
  }, [widgets, effectiveOrder, hiddenWidgets]);

  const toggleWidget = useCallback(
    (key: string) => {
      const next = hiddenWidgets.includes(key)
        ? hiddenWidgets.filter((k) => k !== key)
        : [...hiddenWidgets, key];

      // Panelin tamamen boşalmasını engelle.
      if (effectiveOrder.filter((k) => !next.includes(k)).length === 0) return;

      setHiddenWidgets(next);
      persist(effectiveOrder, next);
    },
    [hiddenWidgets, effectiveOrder, persist],
  );

  const moveWidget = useCallback(
    (key: string, direction: "up" | "down") => {
      const index = effectiveOrder.indexOf(key);
      if (index === -1) return;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= effectiveOrder.length) return;

      const next = [...effectiveOrder];
      [next[index], next[target]] = [next[target], next[index]];
      setWidgetOrder(next);
      persist(next, hiddenWidgets);
    },
    [effectiveOrder, hiddenWidgets, persist],
  );

  const reset = useCallback(() => {
    setWidgetOrder([]);
    setHiddenWidgets([]);
    setIsSaving(true);
    apiClient
      .delete(`${BASE}/${dashboardKey}`)
      .catch(() => undefined)
      .finally(() => setIsSaving(false));
  }, [dashboardKey]);

  const isCustomized =
    hiddenWidgets.length > 0 ||
    (widgetOrder.length > 0 && widgetOrder.join("|") !== defaultOrder.join("|"));

  return { visibleWidgets, descriptors, isSaving, isCustomized, toggleWidget, moveWidget, reset };
}
